import { SOLAR } from "@/constants/testIds";
import { formatMinutesSigned, formatTime, type SolarResult } from "@/lib/solar";
import { zoneCity } from "@/lib/timezone";
import SteadyDigits from "@/components/SteadyDigits";
import SunCompass from "@/components/SunCompass";
import { Row } from "@/components/solar/ui";
import PlaceActions from "@/components/solar/PlaceActions";
import type { Place } from "@/hooks/usePlace";

interface ClockSectionProps {
  place: Place;
  onForget: () => void;
  onLeaveShared: () => void;
  result: SolarResult;
  now: Date;
  placeTz: string;
  tzLabel: string;
}

function PlaceHeading({ place, onForget, onLeaveShared }: Pick<ClockSectionProps, "place" | "onForget" | "onLeaveShared">) {
  return (
    <>
      <p className="font-sans text-xs uppercase tracking-[0.35em] text-[#8a6f52]">True solar time at</p>
      <p className="mt-2 text-sm italic text-[#5e4a35]">
        <span data-testid={SOLAR.placeLabel}>{place.label}</span>
        {place.source === "guess" && (
          <span data-testid={SOLAR.approxNote} className="ml-2 font-sans text-xs not-italic text-[#8a6f52]">
            (approximate, from your time zone)
          </span>
        )}
      </p>
      <PlaceActions place={place} onForget={onForget} onLeaveShared={onLeaveShared} />
    </>
  );
}

function ClockReadout({ result, now, placeTz, tzLabel }: Omit<ClockSectionProps, "place" | "onForget" | "onLeaveShared">) {
  const city = zoneCity(placeTz);
  return (
    <div className="flex flex-col gap-3">
      <Row label="Clock time" testid={SOLAR.clockTime}>
        <SteadyDigits text={formatTime(now, placeTz)} />{" "}
        <span data-testid={SOLAR.clockZone} className="text-[#8a6f52]">
          ({tzLabel}
          {city && ` · ${city}`})
        </span>
      </Row>
      <Row label="Offset from clock" testid={SOLAR.offset}>
        <SteadyDigits text={formatMinutesSigned(result.offsetMinutes)} />
      </Row>
    </div>
  );
}

export default function ClockSection(props: ClockSectionProps) {
  const { result, placeTz } = props;
  return (
    <section className="mb-14 border-y border-[#d9c9ad] py-10 md:py-14">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
        {/* container-type lets the time size itself to this column's width (cqi units) */}
        <div className="min-w-0 flex-1" style={{ containerType: "inline-size" }}>
          <PlaceHeading place={props.place} onForget={props.onForget} onLeaveShared={props.onLeaveShared} />
          <p
            data-testid={SOLAR.trueSolarTime}
            className="mt-4 whitespace-nowrap font-serif leading-none tracking-tight tabular-nums"
            style={{ fontSize: "clamp(2.25rem, 17cqi, 6.5rem)" }}
          >
            <SteadyDigits text={formatTime(result.trueSolarTime, placeTz)} slot="0.56em" />
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-6 lg:items-end lg:text-right">
          <SunCompass altitude={result.altitude} azimuth={result.azimuth} />
          <ClockReadout result={result} now={props.now} placeTz={placeTz} tzLabel={props.tzLabel} />
        </div>
      </div>
    </section>
  );
}
